/**
 * WorkflowLaunchCache - Persists workflow launch state across React strict-mode remounts
 * 
 * Problem: React strict mode mounts components twice, causing location.state to be undefined
 * on second mount. This breaks workflow initialization.
 * 
 * Solution: Cache navigation state in module-level Map BEFORE component mounts,
 * then read from cache in component (survives remounts).
 */

class WorkflowLaunchCache {
  constructor() {
    this.cache = new Map();
    this.ttl = 60000; // 60 second TTL to prevent stale data
  }

  /**
   * Store workflow launch state (call from ClientBriefForm before navigate)
   */
  set(key, state) {
    console.log("🗂️ WorkflowLaunchCache.set:", { key, state });
    this.cache.set(key, {
      state,
      timestamp: Date.now()
    });
  }

  /**
   * Retrieve workflow launch state (call from PlanPage on mount)
   * Returns null if expired or not found
   */
  get(key) {
    const entry = this.cache.get(key);
    
    if (!entry) {
      console.log("🗂️ WorkflowLaunchCache.get: No entry found for", key);
      return null;
    }

    const age = Date.now() - entry.timestamp;
    if (age > this.ttl) {
      console.log("🗂️ WorkflowLaunchCache.get: Entry expired for", key, { age });
      this.cache.delete(key);
      return null;
    }

    console.log("🗂️ WorkflowLaunchCache.get: Retrieved entry for", key, entry.state);
    return entry.state;
  }

  /**
   * Clear specific entry (call after successfully reading state)
   */
  clear(key) {
    console.log("🗂️ WorkflowLaunchCache.clear:", key);
    this.cache.delete(key);
  }

  /**
   * Get the most recent entry (used when location.state is wiped by Strict Mode)
   */
  getLatest() {
    if (this.cache.size === 0) {
      console.log("🗂️ WorkflowLaunchCache.getLatest: No entries");
      return null;
    }

    let latestEntry = null;
    let latestTimestamp = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp > latestTimestamp) {
        latestTimestamp = entry.timestamp;
        latestEntry = { key, ...entry };
      }
    }

    if (latestEntry) {
      const age = Date.now() - latestEntry.timestamp;
      if (age > this.ttl) {
        console.log("🗂️ WorkflowLaunchCache.getLatest: Latest entry expired", { age });
        return null;
      }
      console.log("🗂️ WorkflowLaunchCache.getLatest: Found latest", latestEntry);
      return latestEntry.state;
    }

    return null;
  }

  /**
   * Clear all entries
   */
  clearAll() {
    console.log("🗂️ WorkflowLaunchCache.clearAll");
    this.cache.clear();
  }
}

// Export singleton instance
export const workflowLaunchCache = new WorkflowLaunchCache();
